import React, { useMemo } from 'react';
import { useRootStore } from 'store';
import { TablePaginatedV8, EditCellV8, TableCellV8, TableEditButtons } from 'motion-components';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import { getIn, useFormikContext } from 'formik';
import { useUserLocationPermissions } from 'shared-pages/hooks';
import { useGetMaterialsAsOptionsQuery } from 'shared-pages/components';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import { promiseModalDialog, toastSuccess } from 'motion-components';
import { gql, useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';

interface ITranLinesTableProps {
  internalTransactionLines: {
    id: number;
    aa: number;
    materialId: number;
    containersQty: number;
    netWeight: number;
    material: {
      id: number;
      name: string;
    };
  }[];
}

const gqlDeleteInternalTransactionLine = gql`
  mutation deleteInternalTransactionLine($id: Int) {
    deleteInternalTransactionLine(id: $id)
  }
`;

/**
 * Hook για Εμφάνιση Validation Λάθη
 */
const useInternalTransactionLinesError = (
  errors: any,
  touched: any,
  submitCount: number,
  t: any,
) => {
  return useMemo(() => {
    const internalTransactionLinesError =
      getIn(errors, 'internalTransactionLines') ?? getIn(errors, 'InternalTransactionLines');
    const internalTransactionLinesTouched =
      getIn(touched, 'internalTransactionLines') ?? submitCount > 0;

    const firstNetWeightLineError = Array.isArray(internalTransactionLinesError)
      ? internalTransactionLinesError.reduce<{ rowIndex: number; message: string } | undefined>(
          (acc, lineError, rowIndex) => {
            if (acc) return acc;
            const message = getIn(lineError, 'netWeight');
            if (typeof message === 'string') return { rowIndex, message };
            return undefined;
          },
          undefined,
        )
      : undefined;

    const internalTransactionLinesErrorText =
      typeof internalTransactionLinesError === 'string'
        ? internalTransactionLinesError
        : firstNetWeightLineError
        ? `${t('Γραμμή')} ${firstNetWeightLineError.rowIndex + 1}: ${
            firstNetWeightLineError.message
          }`
        : undefined;

    return {
      internalTransactionLinesErrorText,
      showInternalTransactionLinesError:
        internalTransactionLinesTouched && typeof internalTransactionLinesErrorText === 'string',
    };
  }, [errors, touched, submitCount, t]);
};

/**
 * Πίνακας Απόβλητων Διακίνησης
 * @param param0
 * @returns
 */
const ITranLinesTable = ({ internalTransactionLines: data }: ITranLinesTableProps) => {
  const { appStore, tableStore } = useRootStore();
  const gridRef = React.useRef(null);
  const { canAddNew } = useUserLocationPermissions();
  const [loading, setLoading] = React.useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = React.useState<number | null>(null);
  const insertMaterialDraftRef = React.useRef<{ id: number; name: string } | null>(null);
  const { t } = useTranslation();
  const { values, errors, touched, submitCount, setFieldValue } = useFormikContext<any>();
  const [deleteLine] = useMutation(gqlDeleteInternalTransactionLine);
  const { internalTransactionLinesErrorText, showInternalTransactionLinesError } =
    useInternalTransactionLinesError(errors, touched, submitCount, t);
  const normalizeLines = (lines: any[]) =>
    (Array.isArray(lines) ? lines : []).filter((line) => line && typeof line === 'object');
  const linesData = useMemo(() => normalizeLines(data), [data]);

  const resolveRowIndex = (row: any, currentLines: any[]) => {
    if (tableStore?.state === 'insert') return currentLines.length;

    const rowOriginal = row?.original;

    if (rowOriginal?.id && rowOriginal.id !== '*') {
      const indexById = currentLines.findIndex((line) => line?.id === rowOriginal.id);
      if (indexById >= 0) return indexById;
    }

    const aaValue = Number(rowOriginal?.aa);
    if (Number.isInteger(aaValue) && aaValue > 0) return aaValue - 1;

    const indexValue = Number(row?.index);
    if (Number.isInteger(indexValue) && indexValue >= 0) return indexValue;

    return currentLines.length;
  };

  // Utility function to update `internalTransactionLines`
  const updateinternalTransactionLines = (index, changes) => {
    const rowIndex = Number(index);
    if (!Number.isInteger(rowIndex) || rowIndex < 0) return;

    const currentLines = [...normalizeLines(values?.internalTransactionLines)];
    const existingLine = currentLines[rowIndex];
    const baseLine = existingLine ?? {
      id: null,
      aa: rowIndex + 1,
      materialId: null,
      containersQty: 0,
      netWeight: 0,
      material: { id: null, name: '' },
    };

    while (currentLines.length < rowIndex) {
      currentLines.push({
        id: null,
        aa: currentLines.length + 1,
        materialId: null,
        containersQty: 0,
        netWeight: 0,
        material: { id: null, name: '' },
      });
    }

    currentLines[rowIndex] = {
      ...baseLine,
      ...changes,
      material: { ...(baseLine.material || { id: null, name: '' }), ...(changes?.material || {}) },
    };

    setFieldValue('internalTransactionLines', normalizeLines(currentLines));
  };

  const onChangeMaterial = (value, row) => {
    const formikLines = normalizeLines(values?.internalTransactionLines);
    const currentLines = formikLines.length > 0 ? formikLines : normalizeLines(data);
    const rowIndex = resolveRowIndex(row, currentLines);
    const materialName = row?.valueName ?? '';
    // During insert mode the grid handles temporary row state internally.
    // Writing to Formik here can overwrite an existing row when the temporary row index is unstable.
    if (tableStore?.state === 'insert') {
      insertMaterialDraftRef.current = { id: value, name: materialName };
      return;
    }

    updateinternalTransactionLines(rowIndex, {
      materialId: value,
      material: { id: value, name: materialName },
    });
  };

  const columns = useMemo(
    () => [
      {
        id: 'aa',
        header: t('Αα'),
        columnName: t('Αα'),
        accessorKey: 'aa',
        orderByField: 'aa',
        cell: TableCellV8,
        meta: { type: 'number' },
        size: 30,
      },
      {
        id: 'materialId',
        header: t('Απόβλητο'),
        columnName: t('Απόβλητο'),
        accessorKey: 'materialId',
        orderByField: 'material.name',
        cell: TableCellV8,
        size: 25,
        meta: {
          type: 'select',
          idKey: 'materialId',
          idName: 'material.name',
          hook: useGetMaterialsAsOptionsQuery,
          onValueChange: onChangeMaterial,
        },
      },
      {
        id: 'containersQty',
        header: t('Τεμάχια'),
        columnName: t('Τεμάχια'),
        accessorKey: 'containersQty',
        orderByField: 'containersQty',
        cell: TableCellV8,
        size: 5,
        meta: {
          type: 'number',
        },
      },
      {
        id: 'netWeight',
        header: t('Βάρος (Kg)'),
        columnName: t('Βάρος (Kg)'),
        accessorKey: 'netWeight',
        orderByField: 'netWeight',
        cell: TableCellV8,
        meta: {
          type: 'number',
        },
        size: 30,
      },
      {
        id: 'idocs',
        header: t('Έντυπο Αναγνώρισης'),
        columnName: t('Έντυπο Αναγνώρισης'),
        accessorKey: 'idocs',
        orderByField: 'idocs',
        cell: (props: any) => {
          const { row } = props;
          return (
            <div>
              {row?.original?.idocs?.length > 0
                ? row.original.idocs.map((idoc: any, index: number) => (
                    <React.Fragment key={idoc?.id}>
                      {index > 0 && ', '}
                      <Link to={`/idoc/${idoc?.id}`}>{idoc?.identificationDocument}</Link>
                    </React.Fragment>
                  ))
                : '-'}
            </div>
          );
        },
        size: 20,
      },
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: (props: any) => {
          const { row, table } = props;
          return true ? EditCellV8({ row, table }) : <>-</>;
        },
      },
    ],
    [onChangeMaterial, t],
  );

  const onSaveRow = async (rowIndex, row, changes) => {
    setLoading(true);
    const currentLines = normalizeLines(values?.internalTransactionLines);
    if (row.id === '*') {
      const materialId = changes?.materialId ?? null;
      const draftMaterial = insertMaterialDraftRef.current;
      const materialName =
        changes?.material?.name ??
        row?.original?.material?.name ??
        (draftMaterial?.id === materialId ? draftMaterial.name : '');

      // Insert new row
      const newRow = {
        // id: data.length + 1, // Auto-increment ID
        id: null, // Auto-increment ID
        aa: currentLines.length + 1,
        materialId,
        containersQty: parseFloat(changes?.containersQty) || 0,
        netWeight: parseFloat(changes?.netWeight) || 0,
        material: {
          id: materialId,
          name: materialName,
        },
      };
      setFieldValue('internalTransactionLines', [...currentLines, newRow]);
      insertMaterialDraftRef.current = null;
    } else {
      // Use the utility function to update an existing row
      updateinternalTransactionLines(rowIndex, changes);
    }

    // // Recalculate totals
    // const totalWeight = values.internalTransactionLines.reduce(
    //   (acc, item) => acc + (item.netWeight || 0),
    //   0,
    // );
    // const totalQty = values.internalTransactionLines.reduce((acc, item) => acc + (item.qty || 0), 0);

    // setFieldValue('netWeight', totalWeight);
    // setFieldValue('qty', totalQty);

    setLoading(false);
    return true;
  };

  const handleDeleteRow = async (row: any, index?: number) => {
    const rowIndex = typeof index === 'number' ? index : tableStore?.activeRowIdx;
    if (typeof rowIndex !== 'number' || rowIndex < 0) return false;

    const currentLines = normalizeLines(values?.internalTransactionLines);
    const lineObj = row ?? currentLines[rowIndex];
    if (!lineObj) return false;

    // If new (unsaved) line => remove from Formik only
    if (!lineObj.id) {
      currentLines.splice(rowIndex, 1);
      const reindexed = currentLines.map((l, idx) => ({ ...l, aa: idx + 1 }));
      setFieldValue('internalTransactionLines', reindexed);
      setSelectedRowIndex(null);
      return true;
    }

    const answer = await promiseModalDialog({
      title: t('Διαγραφή Εγγραφής'),
      text: `${t('Θέλετε να διαγράψετε το Απόβλητο')} "${lineObj?.material?.name ?? ''}" ?`,
    });
    if (!answer) return false;

    try {
      await deleteLine({ variables: { id: lineObj.id } });
      toastSuccess(t('Διαγράφηκε το Απόβλητο'));
    } catch (err) {
      console.error(err);
      return false;
    }

    // Remove from Formik
    const idx = currentLines.findIndex((l) => l?.id === lineObj.id);
    if (idx > -1) currentLines.splice(idx, 1);
    const reindexed = currentLines.map((l, i) => ({ ...l, aa: i + 1 }));
    setFieldValue('internalTransactionLines', reindexed);
    setSelectedRowIndex(null);
    return true;
  };

  return (
    <LoadingOverlay active={loading} spinner text={t('Παρακαλώ περιμένετε...')}>
      <div
        className="form-group-title d-flex justify-content-between align-items-center overflow-y-hidden"
        style={{ marginBottom: '0px' }}
      >
        <h5 style={{ marginBottom: '0px' }}>{t('Απόβλητα Διακίνησης')}</h5>
        <TableEditButtons
          className="mr-3"
          canEdit={true}
          canInsert={canAddNew}
          canDelete={true}
          tableOptionsMeta={() => {
            if (gridRef.current) return (gridRef.current as any).table.options.meta;
            else return undefined;
          }}
          twoLines={false}
        />
      </div>

      {/* TableGrid */}
      {linesData ? (
        <>
          <TablePaginatedV8
            style={{ overflow: 'visible' }}
            ref={gridRef}
            loading={false}
            id="containers-grid"
            appStore={appStore}
            tableStore={tableStore}
            key="grid"
            columns={columns as any}
            onSaveRow={onSaveRow}
            deleteRow={handleDeleteRow}
            onNewRow={() => {
              return {
                id: '*',
                isActive: true,
                aa: values.internalTransactionLines.length + 1,
                materialId: null,
                containersQty: 0,
                netWeight: 0,
                material: { id: null, name: '' },
              };
            }}
            onRowClick={(props) => {
              setSelectedRowIndex(props.row.index);
            }}
            rowProps={(row: any) => {
              let style = undefined;
              if (row.index === selectedRowIndex) style = gridRowSelectedStyle;
              if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };

              return { style };
            }}
            data={linesData}
            dataCount={linesData.length}
            topBorderRadius={false}
            initSorting={[{ id: 'aa', desc: false }]}
          />

          {/* Εμφάνισε Validation Λάθη */}
          {showInternalTransactionLinesError && (
            <div className="text-danger select-feedback mt-1">
              <small>{internalTransactionLinesErrorText}</small>
            </div>
          )}
        </>
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
};

export default ITranLinesTable;
